import React from "react";
import { useDesktopNav } from "../navigation/DesktopNavContext";
import { DesktopBackLink } from "../navigation/ScreenBackButton";

/**
 * Shared visual tokens for the Nexora Finance module shell.
 * Keeps Phase 1 screens visually consistent with CrewPilot dark financial UIs.
 */

export const FINANCE_SCREEN_STYLE = {
  minHeight: "100vh",
  padding: "24px 18px 90px",
  color: "white",
  background:
    "radial-gradient(circle at 18% 8%, rgba(78,226,161,0.10), transparent 55%), radial-gradient(circle at 82% 0%, rgba(201,168,76,0.10), transparent 50%), linear-gradient(180deg, rgba(7,10,16,1) 0%, rgba(4,7,12,1) 100%)",
};

export const FINANCE_HEADER_STYLE = {
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  marginBottom: "18px",
};

export const FINANCE_TITLE_STYLE = {
  fontSize: "26px",
  fontWeight: 800,
  letterSpacing: "0.02em",
  margin: 0,
};

export const FINANCE_SUBTITLE_STYLE = {
  opacity: 0.72,
  fontSize: "14px",
  margin: 0,
};

export const FINANCE_CARD_STYLE = {
  background: "var(--nx-surface)",
  border: "1px solid var(--nx-border)",
  borderRadius: "20px",
  padding: "20px 20px 18px",
};

export const FINANCE_CARD_TITLE_STYLE = {
  fontSize: "0.82rem",
  fontWeight: 800,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "rgba(201,168,76,0.95)",
  margin: "0 0 8px",
};

export const FINANCE_COMING_SOON_STYLE = {
  fontSize: "13px",
  color: "rgba(255,255,255,0.45)",
  margin: 0,
};

export const FINANCE_TOOLBAR_STYLE = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
  alignItems: "center",
  marginBottom: "16px",
};

export const FINANCE_PRIMARY_BTN_STYLE = {
  border: "1px solid rgba(78,226,161,0.45)",
  background: "rgba(78,226,161,0.12)",
  color: "#4ee2a1",
  borderRadius: "10px",
  padding: "10px 14px",
  fontWeight: 700,
  cursor: "pointer",
};

export const FINANCE_SECONDARY_BTN_STYLE = {
  border: "1px solid rgba(255,255,255,0.18)",
  background: "rgba(255,255,255,0.04)",
  color: "rgba(255,255,255,0.88)",
  borderRadius: "10px",
  padding: "10px 14px",
  fontWeight: 600,
  cursor: "pointer",
};

export const FINANCE_DANGER_BTN_STYLE = {
  border: "1px solid rgba(248,113,113,0.45)",
  background: "rgba(248,113,113,0.10)",
  color: "#f87171",
  borderRadius: "10px",
  padding: "10px 14px",
  fontWeight: 700,
  cursor: "pointer",
};

export const FINANCE_INPUT_STYLE = {
  width: "100%",
  boxSizing: "border-box",
  borderRadius: "10px",
  border: "1px solid rgba(255,255,255,0.16)",
  background: "rgba(0,0,0,0.28)",
  color: "white",
  padding: "10px 12px",
  fontSize: "14px",
};

export const FINANCE_LABEL_STYLE = {
  display: "block",
  fontSize: "12px",
  opacity: 0.7,
  marginBottom: "6px",
};

export const FINANCE_MODAL_OVERLAY_STYLE = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.75)",
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "20px",
};

export const FINANCE_MODAL_PANEL_STYLE = {
  width: "100%",
  maxWidth: "440px",
  background: "#0b1220",
  border: "1px solid rgba(201,168,76,0.25)",
  borderRadius: "16px",
  padding: "20px",
  color: "white",
};

export function FinanceBackButton({ onClick, label = "Back", primary = false }) {
  const desktopNav = useDesktopNav();
  if (desktopNav) {
    // Desktop: the Finance dashboard is a sidebar destination; sub-screens return to "Finance".
    return primary ? null : (
      <DesktopBackLink onClick={onClick} label={label !== "Back" ? label : desktopNav.parentLabel} />
    );
  }
  return (
    <button
      type="button"
      className="cockpit-back-btn guide-module-back ios-interaction-guard"
      onClick={onClick}
      style={{ alignSelf: "flex-start", marginBottom: "8px" }}
    >
      {label}
    </button>
  );
}

export function FinanceComingSoonCard({ title, description }) {
  return (
    <div style={FINANCE_CARD_STYLE}>
      <h3 style={FINANCE_CARD_TITLE_STYLE}>{title}</h3>
      {description ? (
        <p style={{ ...FINANCE_COMING_SOON_STYLE, marginBottom: "8px" }}>
          {description}
        </p>
      ) : null}
      <p style={FINANCE_COMING_SOON_STYLE}>Coming Soon</p>
    </div>
  );
}

export function FinanceMetricCard({
  title,
  value,
  subtext,
  loading = false,
  error = null,
  onClick = null,
}) {
  const clickable = typeof onClick === "function";
  const content = (
    <>
      <h3 style={FINANCE_CARD_TITLE_STYLE}>{title}</h3>
      {error ? (
        <p style={{ color: "#f87171", margin: 0, fontSize: "13px" }}>{error}</p>
      ) : loading ? (
        <p style={FINANCE_COMING_SOON_STYLE}>Loading…</p>
      ) : (
        <>
          <p
            style={{
              fontSize: "22px",
              fontWeight: 800,
              margin: "0 0 6px",
              letterSpacing: "0.01em",
              color: "#fff",
            }}
          >
            {value}
          </p>
          {subtext ? (
            <p style={{ ...FINANCE_COMING_SOON_STYLE, margin: 0 }}>{subtext}</p>
          ) : null}
        </>
      )}
    </>
  );

  if (clickable) {
    return (
      <button
        type="button"
        onClick={onClick}
        style={{
          ...FINANCE_CARD_STYLE,
          width: "100%",
          textAlign: "left",
          color: "inherit",
          cursor: "pointer",
          font: "inherit",
        }}
      >
        {content}
      </button>
    );
  }

  return <div style={FINANCE_CARD_STYLE}>{content}</div>;
}
